// tslint:disable: completed-docs

import {
    APP,
    CONTAINER_TARIFF,
} from 'Constant';
import {
    all,
    call,
    fork,
    put,
    putResolve,
    select,
    StrictEffect,
    takeLatest,
} from 'redux-saga/effects';
import {
    getHashcode,
    validateHashcode,
} from '@vfde-fe/sails-validation';
import {
    API_CREDENTIALS,
    CREATE_INITIAL_STATE,
    GET_DEVICE_FAILED,
    GO_TO_BASKET,
    HANDLE_PAGESHOW,
    SAILS_PARAM_HARDWAREONLY,
    SAILS_READY_EVENT,
    SET_DEFAULT_STATE,
    TOGGLE_HARDWARE_ONLY,
    TOGGLE_INSURANCE,
    TRIGGER_GIGAKOMBI_OVERLAY,
} from './constants';
import {
    selectGigakombi,
    selectSalesChannel,
    selectSubscription,
    selectSubscriptionId,
} from '../Tariff/selectors';
import {
    ApiErrorResponseWithCode,
    getSessionStorageItemJson,
    request,
    RequestResponse,
    SelectorReturnType,
    setSessionStorageItemJson,
    SHOP_URL,
    triggerOverlay,
} from '@vfde-fe/sails_core';
import {
    getGigakombi,
    getGigakombiEligibilityToken,
    getRestlaufzeit,
    getSaleschannel,
    getSubscriptionId,
} from '../Tariff/helper/getSubscriptionDataHelper';
import mountOptionsContainer from '../Options';
import {
    ADDITIONAL_PAGE_OPTIONS,
    API_BTX_BNT,
    INSURANCE_ID,
    MAP_SALESCHANNEL_TO_API_SALESCHANNEL,
    MAP_SALESCHANNEL_TO_GROUP_ID,
    RED_L_GROUP,
    RED_XL_GROUP,
    RedTariff,
    SAILS_BNT_STORAGE,
    SAILS_PAGE_OPTIONS,
    SAILS_PARAM_GIGAKOMBI,
    SAILS_PARAM_INSURANCE,
    SAILS_PARAM_SALESCHANNEL,
    SAILS_PARAM_SUB_ID,
    SAILS_PARAM_TAUSCHBONUS,
    SAILS_PARAM_TRADE_IN,
    SalesChannel,
    SALESCHANNEL_CONSUMER,
    SALESCHANNEL_FAMILY_FRIENDS,
    SALESCHANNEL_REDPLUS,
    SALESCHANNEL_SOHO,
    YOUNG_L_PLUS_GROUP,
    YoungTariff,
} from '@vfde-fe/sails-constants';
import { GIGAKOMBI_OVERLAY_NAME } from '../Overlay/constant';
import {
    selectHardwareOnly,
    selectInsurance,
    selectInsuranceSelected,
} from './selectors';
import merge from 'lodash.merge';
import {
    getHardwareOnly,
    getInsurance,
    getTauschbonus,
    getTradeIn,
} from './helpers/getCustomerConfig';
import { trackInsurance } from 'Helper/tracking';
import { trackPageView } from '../Options/helpers/tracking';
import { createSailsReadyEvent } from './helpers/publicFunctions';
import { selectAtomicId } from '../Options/selectors';
import {
    BASKET_PROPOSITION_BNT,
    BASKET_PROPOSITION_HARDWAREONLY,
    BASKET_PROPOSITION_REDPLUS,
    createBasket,
    createBasketItem,
    createBasketProposition,
    generateDeeplink,
    getApiCredentials,
} from '@vfde-fe/utils';
import {
    getInsuranceFromSails,
    updateStorageSaga,
} from '@vfde-fe/sails-storage';
import { getIsRestlaufzeitEligible } from './helpers/getIsRestlaufzeitEligible';
import {
    selectTauschbonus,
    selectTradeIn,
} from '../TradeIn/selectors';
import { getIsTauschbonusEligible } from './helpers/getIsTauschbonusEligible';
import { getTauschbonusSoc } from './helpers/getTauschbonusSoc';
import {
    HardwareDetailsResponse,
    HardwareDetailsResponseError,
    Subscription,
} from './interfaces/api';
import { SailsHardwareDetailsStorage } from './interfaces/storage';
import { deviceDetailActionCreators } from './actions';
import { tariffActionCreators } from '../Tariff/actions';

/**
 * hardwareDetail saga
 * Watches actions
 */
export default function* hardwareDetailSaga (): Generator<StrictEffect> {
    yield takeLatest(CREATE_INITIAL_STATE, createInitialStateSaga);
    yield takeLatest(TRIGGER_GIGAKOMBI_OVERLAY, triggerGigaKombiOverlay);
    yield takeLatest(TOGGLE_HARDWARE_ONLY, toggleHardwareOnlySaga);
    yield takeLatest(TOGGLE_INSURANCE, setInsuranceSaga);
    yield takeLatest(SET_DEFAULT_STATE, toggleHardwareOnlySaga);
    yield takeLatest(GO_TO_BASKET, goToBasketSaga);
    yield takeLatest(GET_DEVICE_FAILED, getDeviceFailedSaga);
    yield takeLatest(HANDLE_PAGESHOW, handlePageShowSaga);
}

/**
 * createInitialState
 */
export function* createInitialStateSaga (action: ReturnType<typeof deviceDetailActionCreators.createInitialState>): Generator<StrictEffect> {
    yield put(deviceDetailActionCreators.showLoading());

    const { deviceId } = window[ADDITIONAL_PAGE_OPTIONS];
    const salesChannel = (yield call(getSaleschannel)) as SalesChannel;
    const subscriptionId = (yield call(getSubscriptionId, salesChannel)) as ReturnType<typeof getSubscriptionId>;

    if (salesChannel === SALESCHANNEL_FAMILY_FRIENDS) {
        const hashcode = (yield call(getHashcode)) as ReturnType<typeof getHashcode>;
        yield putResolve(validateHashcode(hashcode));
    }

    const gigakombi = (yield call(getGigakombi, salesChannel)) as ReturnType<typeof getGigakombi>;
    const insurance = (yield call(getInsurance)) as ReturnType<typeof getInsurance>;
    const isHardwareOnly = (yield call(getHardwareOnly)) as ReturnType<typeof getHardwareOnly>;
    const isTradeIn = (yield call(getTradeIn)) as ReturnType<typeof getTradeIn>;
    const isTauschbonus = (yield call(getTauschbonus, isTradeIn)) as ReturnType<typeof getTauschbonus>;
    const gigakombiToken = (yield call(getGigakombiEligibilityToken)) as ReturnType<typeof getGigakombiEligibilityToken>;

    yield put(tariffActionCreators.setDefaultState(salesChannel, MAP_SALESCHANNEL_TO_GROUP_ID[salesChannel].indexOf(subscriptionId!) !== -1 ? subscriptionId : null, gigakombi, gigakombiToken || null));
    yield put(deviceDetailActionCreators.setDefaultState(isHardwareOnly, insurance, isTradeIn, isTauschbonus));
    yield call(getDeviceSaga, deviceId);
    yield call(updateStorageSaga, SAILS_BNT_STORAGE, {
        [SAILS_PARAM_TAUSCHBONUS]: isTauschbonus,
    });
    yield call(createSailsReadyEvent, SAILS_READY_EVENT, action.actions);
}

const getDevicesPayload = (
    {
        deviceId,
        salesChannel,
        subscriptionId,
        gigakombi,
        isTradeInEligible,
        isTauschbonusEligible,
    }: {
        deviceId: string;
        salesChannel: NonNullable<SelectorReturnType<typeof selectSalesChannel>>;
        subscriptionId: SelectorReturnType<typeof selectSubscriptionId>;
        gigakombi: SelectorReturnType<typeof selectGigakombi>;
        isTradeInEligible: boolean;
        isTauschbonusEligible: boolean;
    },
) => {
    const payload: {
        productIds: string[];
        offerBtx: string;
        offerSalesChannel: string;
        subscriptionIds: string[];
        discounts: string[],
    } = {
        productIds: [
            deviceId.toString(),
        ],
        offerBtx: API_BTX_BNT,
        offerSalesChannel: MAP_SALESCHANNEL_TO_API_SALESCHANNEL[salesChannel],
        subscriptionIds: MAP_SALESCHANNEL_TO_GROUP_ID[salesChannel].filter(value => {
            if (SALESCHANNEL_FAMILY_FRIENDS === salesChannel) {
                // Remove Red XL and Young L+ from subscription IDs for F&F
                return value !== RED_XL_GROUP && value !== YOUNG_L_PLUS_GROUP;
            }

            // Remove Red L and Young L+ from the subscription IDs sent to the API for the device first use case
            return value !== RED_L_GROUP && value !== YOUNG_L_PLUS_GROUP;
        }).map(value => value.toString()),
        discounts: [],
    };

    const { salesChannel: discountForSalesChannel, gigakombi: gigakombiDiscount, promotions } = window[SAILS_PAGE_OPTIONS].discounts;
    if (gigakombi) {
        payload.discounts.push(gigakombiDiscount.marker);
        const dummySoc = subscriptionId && (gigakombiDiscount.dummy[salesChannel]?.[subscriptionId as RedTariff | YoungTariff] || gigakombiDiscount.dummy[salesChannel]?.default);
        dummySoc && payload.discounts.push(dummySoc);
    }

    if (salesChannel === SALESCHANNEL_FAMILY_FRIENDS) {
        // Replace discounts array from Gigakombi because Gigakombi and F&F are not combinable
        payload.discounts = [
            discountForSalesChannel[SALESCHANNEL_FAMILY_FRIENDS],
        ];
    }
    // Quickfix for iPhone Promo to hardcore discount id, discount will be ignored to damian when its not available on a device in vlux @TODO: remove when promo is gone
    payload.discounts.push(promotions.iphone);

    // Add other discounts: Soho and Tradein
    salesChannel === SALESCHANNEL_SOHO && payload.discounts.push(discountForSalesChannel[SALESCHANNEL_SOHO].default, discountForSalesChannel[SALESCHANNEL_SOHO][RED_XL_GROUP]);
    isTradeInEligible && payload.discounts.push(promotions.tradeIn);

    if (isTauschbonusEligible) {
        const tauschbonusSoc = getTauschbonusSoc(deviceId);
        tauschbonusSoc && payload.discounts.push(tauschbonusSoc);
    }

    if (subscriptionId) {
        payload.subscriptionIds = [
            subscriptionId.toString(),
        ];
    }

    for (const defaultDiscount of promotions.default) {
        salesChannel !== SALESCHANNEL_FAMILY_FRIENDS && payload.discounts.push(defaultDiscount);
    }

    return payload;
};

/** * getDevice saga
 * Fetches the API data
 */
export function* getDeviceSaga (deviceId: string): Generator<StrictEffect> {
    const [salesChannel, subscriptionId, gigakombi] = (yield all([
        select(selectSalesChannel()),
        select(selectSubscriptionId()),
        select(selectGigakombi()),
    ])) as [
        NonNullable<SelectorReturnType<typeof selectSalesChannel>>,
        SelectorReturnType<typeof selectSubscriptionId>,
        SelectorReturnType<typeof selectGigakombi>,
    ];

    const isTauschbonusEligible = (yield call(getIsTauschbonusEligible)) as ReturnType<typeof getIsTauschbonusEligible>;
    const isTradeInEligible = isTauschbonusEligible;

    const {key, url} = getApiCredentials(API_CREDENTIALS);
    const payload = getDevicesPayload({
        deviceId,
        salesChannel,
        subscriptionId,
        gigakombi,
        isTradeInEligible,
        isTauschbonusEligible,
    });
    const options = {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-key': key,
        },
        body: JSON.stringify(payload),
    };

    try {
        const response = (yield call(request, url, options)) as RequestResponse<HardwareDetailsResponse>;
        const { status, body } = response;

        if (status === 200) {
            yield put(deviceDetailActionCreators.getDeviceSuccess(body!));
            yield call(mountOptionsContainer);
        }
    } catch (err) {
        if (err instanceof ApiErrorResponseWithCode && err.status === 400) {
             const { body } = err as ApiErrorResponseWithCode<HardwareDetailsResponseError>;
            yield put(deviceDetailActionCreators.getDeviceFailed(body));
        } else {
            console.error(err);
        }
    }
}

/**
 * Get device failed saga
 */
export function* getDeviceFailedSaga (action: ReturnType<typeof deviceDetailActionCreators.getDeviceFailed>): Generator<StrictEffect> {
    const errorCode = action.error.code;
    let errorPageUrl: string | undefined;

    switch (errorCode) {
        case '400.05':
            errorPageUrl = window[ADDITIONAL_PAGE_OPTIONS].apiError?.[errorCode];
            break;
    }

    if (errorPageUrl) {
        yield call([window.location, 'assign'], errorPageUrl);
    }
}

/**
 * Gigakombi overlay
 */
export function* triggerGigaKombiOverlay (): Generator<StrictEffect> {
    const url = (yield call(generateDeeplinkSaga)) as string;
    yield call(setSessionStorageItemJson, 'deeplink', url.replace(SHOP_URL, ''));
    const {beforeOpenGigaKombiOverlayLegacy} = window as any;
    beforeOpenGigaKombiOverlayLegacy && beforeOpenGigaKombiOverlayLegacy();
    yield put(triggerOverlay(undefined, GIGAKOMBI_OVERLAY_NAME));
}

/**
 * Toggle Hardware
 */
export function* toggleHardwareOnlySaga (): Generator<StrictEffect> {
    const [
        isHardwareOnly,
        storage,
        insuranceSelected,
    ] = (yield all([
        select(selectHardwareOnly()),
        call(getSessionStorageItemJson, SAILS_BNT_STORAGE),
        select(selectInsuranceSelected()),
    ])) as [
        SelectorReturnType<typeof selectHardwareOnly>,
        SailsHardwareDetailsStorage | null,
        SelectorReturnType<typeof selectInsuranceSelected>,
    ];

    const deviceId = window[ADDITIONAL_PAGE_OPTIONS].deviceId;

    // Store hardware only state per hubpage
    const newStorage = merge({}, storage || {}, {
        [APP]: {
            [deviceId]: {
                [SAILS_PARAM_HARDWAREONLY]: isHardwareOnly,
            },
        },
        [SAILS_PARAM_INSURANCE]: insuranceSelected,
    }) as Partial<SailsHardwareDetailsStorage>;

    if (isHardwareOnly) {
        // Remove subscription related stuff from session storage and save it for later, because old NSF does NOT like it when hardware only is true...
        // We check for subscription data in old backup storage first, then in main storage.
        const tariffStorage = storage?.[APP]?.[CONTAINER_TARIFF] ? storage[APP][CONTAINER_TARIFF] : storage;
        const {
            [SAILS_PARAM_SUB_ID]: activeSubscriptionId,
            [SAILS_PARAM_GIGAKOMBI]: gigakombi,
            [SAILS_PARAM_INSURANCE]: insurance,
            [SAILS_PARAM_TRADE_IN]: tradeIn,
            [SAILS_PARAM_SALESCHANNEL]: salesChannel,
        } = tariffStorage || {};

        if (activeSubscriptionId || gigakombi || insurance || tradeIn || salesChannel) {
            // Create tariff storage inside SAILS_BNT_STORAGE, so we keep track on the previous flags before hardware only was switched on
            newStorage[APP]![CONTAINER_TARIFF] = (({ activeSubscriptionId, gigakombi, insurance, tradeIn, salesChannel }) => ({activeSubscriptionId, gigakombi, insurance, tradeIn, salesChannel}))(tariffStorage!);
        }
        // We have to set the saleschannel to consumer when toggling hardwareOnly, as the basket only accepts this channel for hardwareOnly
        newStorage[SAILS_PARAM_SALESCHANNEL] = SALESCHANNEL_CONSUMER;
        delete newStorage[SAILS_PARAM_SUB_ID];
        delete newStorage[SAILS_PARAM_GIGAKOMBI];
        delete newStorage[SAILS_PARAM_INSURANCE];
        delete newStorage[SAILS_PARAM_TRADE_IN];
    } else {
        // If there is a subscription backup, put it back where it belongs
        if (storage?.[APP]?.[CONTAINER_TARIFF]) {
            merge(newStorage, storage[APP][CONTAINER_TARIFF]);
            delete newStorage[APP]![CONTAINER_TARIFF];
        }
    }

    yield call(setSessionStorageItemJson, SAILS_BNT_STORAGE, newStorage);
    yield call(trackPageView);
}

/**
 * Set insurance status in session storage
 */
export function* setInsuranceSaga (action: ReturnType<typeof deviceDetailActionCreators.toggleInsurance>): Generator<StrictEffect> {
    const { trackIt } = action;
    const [
        insurance,
        insuranceSelected,
    ] = (yield all([
        select(selectInsurance()),
        select(selectInsuranceSelected()),
    ])) as [
        Exclude<SelectorReturnType<typeof selectInsurance>, undefined>,
        SelectorReturnType<typeof selectInsuranceSelected>,
    ];
    yield call(updateStorageSaga, SAILS_BNT_STORAGE, {
        [SAILS_PARAM_INSURANCE]: insuranceSelected,
    });

    if (trackIt) {
        yield fork(trackInsurance, { insurance, insuranceSelected });
    }
}

/**
 * Go to basket
 */
export function* goToBasketSaga (): Generator<StrictEffect> {
    const url = (yield call(generateDeeplinkSaga)) as string;
    yield call([window.location, 'assign'], url);
}

/**
 * Generate deeplink
 */
export function* generateDeeplinkSaga (): Generator<StrictEffect, string> {
    const serviceIds: string[] = [];
    const discountIds: string[] = [];
    const [
        salesChannel,
        atomicId,
        hasInsurance,
        isGigakombi,
        tariffId,
        subscription,
        isHardwareOnly,
        isRestlaufzeit,
        isTradeIn,
        isTauschbonus,
    ] = (yield all([
        select(selectSalesChannel()),
        select(selectAtomicId()),
        select(selectInsuranceSelected()),
        select(selectGigakombi()),
        select(selectSubscriptionId()),
        select(selectSubscription()),
        select(selectHardwareOnly()),
        call(getRestlaufzeit),
        select(selectTradeIn()),
        select(selectTauschbonus()),
    ])) as [
        NonNullable<SelectorReturnType<typeof selectSalesChannel>>,
        SelectorReturnType<typeof selectAtomicId>,
        SelectorReturnType<typeof selectInsuranceSelected>,
        SelectorReturnType<typeof selectGigakombi>,
        SelectorReturnType<typeof selectSubscriptionId>,
        SelectorReturnType<typeof selectSubscription>,
        SelectorReturnType<typeof selectHardwareOnly>,
        ReturnType<typeof getRestlaufzeit>,
        SelectorReturnType<typeof selectTradeIn>,
        SelectorReturnType<typeof selectTauschbonus>,
    ];

    if (!isHardwareOnly) {
        (hasInsurance && atomicId) && serviceIds.push(INSURANCE_ID);
        const { gigakombi, promotions } = window[SAILS_PAGE_OPTIONS].discounts;
        if (isGigakombi) {
            serviceIds.push(gigakombi.marker);
            // Gigakombi dummy socs are not available for unlimited XL tariffs
            const dummySoc = gigakombi.dummy[salesChannel]?.[tariffId! as RedTariff | YoungTariff] || gigakombi.dummy[salesChannel]?.default;
            dummySoc && serviceIds.push(dummySoc);
        }

        // Quickfix for iPhone Promo to hardcore discount id, discount will be ignored to damian when its not available on a device in vlux @TODO: remove when promo is gone
        discountIds.push(promotions.iphone);

        // add trade in soc if trade-in is true
        if (isTradeIn) {
            discountIds.push(promotions.tradeIn);

            if (isTauschbonus) {
                const deviceId = window[ADDITIONAL_PAGE_OPTIONS].deviceId as string;
                const tauschbonusSoc = getTauschbonusSoc(deviceId);
                tauschbonusSoc && discountIds.push(tauschbonusSoc);
            }
        }

        const isRestlaufzeitEligible = getIsRestlaufzeitEligible(isGigakombi, salesChannel, tariffId);
        isRestlaufzeitEligible && isRestlaufzeit && serviceIds.push(promotions.rlz);
        serviceIds.push(...promotions.default);
    }

    const basketItem = createBasketItem(!isHardwareOnly ? (subscription as Subscription).id.toString() : undefined, atomicId?.toString(), discountIds, serviceIds);

    const propositionType = isHardwareOnly ? BASKET_PROPOSITION_HARDWAREONLY : salesChannel === SALESCHANNEL_REDPLUS ? BASKET_PROPOSITION_REDPLUS : BASKET_PROPOSITION_BNT;
    const proposition = createBasketProposition(propositionType, [basketItem]);

    const basket = createBasket(salesChannel, [proposition]);

    return generateDeeplink(basket, true);
}

/**
 * Handle pageshow saga
 * On cached pageshow (e.g. afer forwards or backwards navigation)
 * trigger a `toggleInsurance` action to make sure, the feature-card and price
 * is up-to-date based on the session-storage property.
 * This is relevant, if the user selects or deselects insurance on the
 * Connect-More-Page and uses the browser-back-button which brings him back to DDP again.
 */
export function* handlePageShowSaga (action: ReturnType<typeof deviceDetailActionCreators.handlePageShow>): Generator<StrictEffect> {
    const event = action.event as PageTransitionEvent;

    if (!event.persisted) {
        // we only care for cached page navigations
        return;
    }

    const sailsBnt = (yield call(getSessionStorageItemJson, SAILS_BNT_STORAGE)) as SailsHardwareDetailsStorage | null;

    if (!sailsBnt) {
        return;
    }

    const hasInsurance = (yield call(getInsuranceFromSails, sailsBnt)) as boolean;

    yield put(deviceDetailActionCreators.toggleInsurance(hasInsurance, true, false));
}
